import React from 'react';

import { Label, Select } from '@auspost/postmaster-react';

import NextOptionSvg from 'assets/next-dropdown-option.svg';
import PreviousOptionSvg from 'assets/previous-dropdown-option.svg';
import { selectCurrentArticleIndex } from 'store/consigmentSwitcher';
import {
  nextArticle,
  previousArticle,
  updateCurrentArticleIndex,
} from 'store/consigmentSwitcher/consignmentSwitcher.thunk';
import { useAppDispatch, useAppSelector } from 'store/store';

import { useArticles, useShipment } from '../../hooks/shipment.hooks';
import {
  CONSIGNMENT_SWITCHER_CHEVRON_LEFT_TEST_ID,
  CONSIGNMENT_SWITCHER_CHEVRON_RIGHT_TEST_ID,
  CONSIGNMENT_SWITCHER_DROPDOWN_TEST_ID,
  CONSIGNMENT_SWITCHER_TEST_ID,
} from './ConsignmentSwitcher.test-ids';

import styles from './ConsignmentSwitcher.module.scss';

export interface DropdownOptions {
  label: string;
  value: number;
  'aria-label'?: string;
}

function truncatedArticleId(articleId: string, sliceStartLength: number, sliceEndLength: number) {
  if (articleId.length <= 18) {
    return articleId;
  }
  return `${articleId.slice(0, sliceStartLength)}...${articleId.slice(-sliceEndLength)}`;
}

const ConsignmentSwitcher: React.FC = () => {
  const dispatch = useAppDispatch();
  const shipment = useShipment();
  const articles = useArticles();
  const currentArticleIndex = useAppSelector(selectCurrentArticleIndex);

  if (articles.length <= 1 || !shipment) return null;

  const dropdownOptions: DropdownOptions[] = articles.map((article, index) => ({
    label: `Parcel ${index + 1} of ${articles.length} - ${truncatedArticleId(article.articleId, 12, 6)}`,
    value: index,
    'aria-label': `tracking number ending in ${article.articleId.slice(-6)}`,
  }));

  const onArticleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(updateCurrentArticleIndex(+e.target.value));
  };

  const onPrevSelect = () => {
    dispatch(previousArticle(articles.length));
  };

  const onNextSelect = () => {
    dispatch(nextArticle(articles.length));
  };

  return (
    <div className={styles.consignmentSwitcherWrapper} data-testid={CONSIGNMENT_SWITCHER_TEST_ID}>
      <div className={styles.divider} />
      <Label htmlFor='consignmentDropdown'>Select parcel</Label>
      <div className={styles.consignmentDropdown}>
        <Select
          id='consignmentDropdown'
          variant='small'
          options={dropdownOptions}
          value={currentArticleIndex}
          onChange={onArticleSelect}
          data-testid={CONSIGNMENT_SWITCHER_DROPDOWN_TEST_ID}
        />
        <div className={styles.consignmentDropdownButtonGroup}>
          <button
            aria-label='Previous parcel in shipment'
            onClick={onPrevSelect}
            data-testid={CONSIGNMENT_SWITCHER_CHEVRON_LEFT_TEST_ID}
          >
            <PreviousOptionSvg />
          </button>
          <button
            aria-label='Next parcel in shipment'
            onClick={onNextSelect}
            data-testid={CONSIGNMENT_SWITCHER_CHEVRON_RIGHT_TEST_ID}
          >
            <NextOptionSvg />
          </button>
        </div>
        <span className='sr-only'>Showing tracking details for {articles[currentArticleIndex].articleId}</span>
      </div>
    </div>
  );
};

export default ConsignmentSwitcher;
